import type {
  BlockAudio,
  BlockFile,
  BlockH5P,
  BlockImage,
  BlockScorm,
  BlockVideo,
  Course
} from 'shared-types';

type SupportedBlocks =
  | BlockAudio
  | BlockFile
  | BlockH5P
  | BlockImage
  | BlockScorm
  | BlockVideo;

const isVideoBlock = (data: SupportedBlocks): data is BlockVideo => {
  return 'source' in data;
};

interface Params {
  data: SupportedBlocks;
  courseLanguage: Course['language'];
  editorActiveLanguageVersion?: string;
  showLanguageSpecificUploader: boolean;
}

const getBlockFileForLanguage = ({
  data,
  courseLanguage,
  editorActiveLanguageVersion,
  showLanguageSpecificUploader
}: Params) => {
  // check which language version is being edited
  const activeLanguage = editorActiveLanguageVersion || courseLanguage;
  const isVideo = isVideoBlock(data);

  // check if currently editing a language version instead of the default language of the course
  const isEditingLanguageVersion =
    Boolean(editorActiveLanguageVersion) &&
    editorActiveLanguageVersion !== courseLanguage;

  // check if there is a language specific file for the active language
  const hasFile = Boolean(data.locales?.[activeLanguage]?.file?.url);

  let hasSource = false;
  if (isVideo) {
    hasSource = Boolean(
      data.locales?.[activeLanguage]?.source &&
        data.locales?.[activeLanguage]?.remote_id
    );
  }

  const hasLanguageSpecificFile = hasFile || hasSource;

  // check if we should show the default file
  const shouldShowDefaultFile =
    isEditingLanguageVersion &&
    !hasLanguageSpecificFile &&
    !showLanguageSpecificUploader;

  const language = shouldShowDefaultFile ? courseLanguage : activeLanguage;

  // get relevant elements
  const fileUrl = data.locales?.[language]?.file?.url;
  if (isVideo) {
    const source = data.locales?.[language]?.source;
    const remote_id = data.locales?.[language]?.remote_id;
    const remote_hash = data.locales?.[language]?.remote_hash;

    return {
      activeLanguage,
      shouldShowDefaultFile,
      fileUrl,
      source,
      remote_id,
      remote_hash
    };
  }

  return { activeLanguage, shouldShowDefaultFile, fileUrl };
};

export default getBlockFileForLanguage;
